import { computed, signal } from "@preact/signals";
import type {
    DynamicSearchTerm,
    MatchResult,
    SearchTerm,
    TextSearchTerm,
} from "./interfaces";

const matchSimpleTerm = (query: string, term: TextSearchTerm) =>
    term.aliases.some((text) => text.startsWith(query));

const makeSearch = () => {
    const terms = signal<SearchTerm[]>([]);
    const results = signal<MatchResult[]>([]);
    const loading = signal(false);
    const simpleTerms = computed(() =>
        terms.value
            .filter(
                (term): term is TextSearchTerm => typeof term !== "function",
            )
            .map((term) => ({
                ...term,
                aliases: term.aliases.map((text) =>
                    text.replace(/\W/g, "").toLowerCase(),
                ),
            })),
    );
    const dynamicTerms = computed(() =>
        terms.value.filter(
            (term): term is DynamicSearchTerm => typeof term === "function",
        ),
    );

    const search = async (query: string) => {
        if (!query || !terms.value.length) {
            results.value = [];
            return;
        }

        loading.value = true;
        results.value = [];

        const pureQuery = query.replace(/\W/g, "").toLowerCase();

        const textResults = simpleTerms.value.filter((term) =>
            matchSimpleTerm(pureQuery, term),
        );

        results.value = textResults;

        const dynamicResults = (
            await Promise.all(
                dynamicTerms.value.map(async (term) => await term(query)),
            )
        ).flat();

        if (dynamicResults.length) {
            results.value = results.value.concat(dynamicResults);
        }

        loading.value = false;
    };

    return { terms, results, loading, search };
};

export default makeSearch;
